import router from 'next/router'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  VFC,
} from 'react'
import { useRoomTags } from 'src/api/companyMessageRoomTag'
import { useNegativeBadges } from 'src/api/negativeBadge'
import { useRoom } from 'src/api/room'
import { CompanyMessageRoomTag } from 'src/api/types/companyMessageRoomTag'
import { NegativeBadge } from 'src/models/NegativeBadge'
import { Room, RoomUniqueId } from 'src/models/Room'
import { LoginUser } from 'src/models/User'
import { greet } from 'src/utils/routes'
import { SWRInfiniteResponse } from 'swr/infinite'
import { isRoomIdFormat } from './utils'

const MessageContext = createContext<{
  room?: Room
  negativeBadges: NegativeBadge[]
  isReportModalOpen: boolean | undefined
  setReportModalOpen: (_isReportModalOpen: boolean | undefined) => void
  viewingUserId: number | undefined
  setViewingUserId: (_viewingUserId: number | undefined) => void
  mutate: SWRInfiniteResponse<Room>['mutate'] | undefined
  tags: CompanyMessageRoomTag[]
  mutateTags: SWRInfiniteResponse<CompanyMessageRoomTag>['mutate'] | undefined
  editTagId: number | undefined
  setEditTagId: (_editTagId: number | undefined) => void
  isDeleteTagModalOpen: boolean | undefined
  setIsDeleteTagModalOpen: (_isDeleteTagModalOpen: boolean | undefined) => void
  editTagName: string
  setEditTagName: (_editTagId: string) => void
  isRenameTagModalOpen: boolean | undefined
  setIsRenameTagModalOpen: (_isRenameTagModalOpen: boolean | undefined) => void
  tagValidationMessage: string
  setTagValidationMessage: (_tagValidationMessage: string) => void
  isTagValidationModalOpen: boolean | undefined
  setIsTagValidationModalOpen: (
    _isTagValidationModalOpen: boolean | undefined,
  ) => void
}>({
  room: undefined,
  negativeBadges: [],
  isReportModalOpen: undefined,
  setReportModalOpen: () => {},
  viewingUserId: undefined,
  setViewingUserId: () => {},
  mutate: undefined,
  tags: [],
  mutateTags: undefined,
  editTagId: undefined,
  setEditTagId: () => {},
  isDeleteTagModalOpen: undefined,
  setIsDeleteTagModalOpen: () => {},
  editTagName: '',
  setEditTagName: () => {},
  isRenameTagModalOpen: undefined,
  setIsRenameTagModalOpen: () => {},
  tagValidationMessage: '',
  setTagValidationMessage: () => {},
  isTagValidationModalOpen: undefined,
  setIsTagValidationModalOpen: () => {},
})

export type RoomsQuery = {
  id?: RoomUniqueId // same with room_id: for shortening
  room_id?: RoomUniqueId
}
export const MessageContextProvider: VFC<{
  children: ReactNode
  user: LoginUser
  query: RoomsQuery
}> = ({ children, user, query }) => {
  const [roomId, setRoomId] = useState<RoomUniqueId | undefined>()
  const { data: rooms, mutate } = useRoom(roomId, user.id)
  const queriedRoomId = query.id || query.room_id

  const [viewingUserId, setViewingUserId] = useState<number | undefined>()
  const [isReportModalOpen, setReportModalOpen] = useState<boolean>()
  const [editTagId, setEditTagId] = useState<number | undefined>(undefined)
  const [editTagName, setEditTagName] = useState<string>('')
  const [isDeleteTagModalOpen, setIsDeleteTagModalOpen] = useState<boolean>()
  const [isRenameTagModalOpen, setIsRenameTagModalOpen] = useState<boolean>()
  const [tagValidationMessage, setTagValidationMessage] = useState<string>('')
  const [isTagValidationModalOpen, setIsTagValidationModalOpen] =
    useState<boolean>()
  const { data: negativeBadges = [] } = useNegativeBadges(
    rooms ? rooms[0]?.counters[0].id : 0,
  )

  const { data: tags, mutate: mutateTags } = useRoomTags()

  useEffect(() => {
    if (rooms && !rooms.length)
      router.replace(router.pathname, undefined, { shallow: true })
  }, [rooms])

  useEffect(() => {
    if (!rooms || !rooms.length) return
    // NOTE: Rails の CompanyMessageRoomsController#show と条件を合わせる
    if (!user.plan.isAdminRole && !user.plan.isAdminRole && rooms[0].new)
      router.replace(greet(rooms[0].uniqueId))
  }, [rooms, user.plan.isAdminRole])

  useEffect(() => {
    if (queriedRoomId && !isRoomIdFormat(queriedRoomId)) {
      router.replace(router.pathname, undefined, { shallow: true })
      return
    }
    setRoomId(queriedRoomId)
  }, [queriedRoomId])
  return (
    <MessageContext.Provider
      value={{
        room: rooms ? rooms[0] : undefined,
        negativeBadges: negativeBadges || [],
        isReportModalOpen: isReportModalOpen || false,
        setReportModalOpen,
        viewingUserId: viewingUserId || undefined,
        setViewingUserId,
        mutate: mutate || undefined,
        tags: tags || [],
        mutateTags,
        editTagId: editTagId || undefined,
        setEditTagId,
        isDeleteTagModalOpen: isDeleteTagModalOpen || false,
        setIsDeleteTagModalOpen,
        editTagName,
        setEditTagName,
        isRenameTagModalOpen: isRenameTagModalOpen || false,
        setIsRenameTagModalOpen,
        tagValidationMessage,
        setTagValidationMessage,
        isTagValidationModalOpen: isTagValidationModalOpen || false,
        setIsTagValidationModalOpen,
      }}
    >
      {children}
    </MessageContext.Provider>
  )
}

export const useMessageContext = () => useContext(MessageContext)
